// Los textos de la cartera que dependen del estado: píldoras parciales, avisos de lo cargado,
// tabla vacía y errores del listado. Puro.
import { ApiError, INTERNAL_ERROR_MESSAGE } from '@/api/errors';
import {
    countWithNoun,
    CUSTOMERS_NOUN,
    loadedCountTitle,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import type { CustomersSegment, SegmentCounts } from './portalCustomersFilters';

/** Qué cuenta cada píldora, para su `title`. */
export const SEGMENT_TITLE: Record<CustomersSegment, string> = {
    all: 'Clientes que te han comprado y los que abrieron su cuenta en tu tienda',
    active: 'Última compra contigo hace 30 días o menos',
    dormant: 'Más de 90 días sin comprarte',
    repurchase: 'Compró a crédito SK hace 60 días o más, o es recurrente y lleva 90 o más',
    recurrent: 'Dos compras contigo o más',
    skCredit: 'Te compró a crédito SureKeep al menos una vez',
    accountOnly: 'Abrió su cuenta PCF en tu tienda y aún no compra',
};

export interface PillCount {
    value: string;
    title: string;
}

/** Número y explicación de cada píldora, sobre lo cargado; lo parcial lo dicen el `title` y el aviso. */
export function pillCounts(
    counts: SegmentCounts,
    scope: LoadedScope,
): Record<CustomersSegment, PillCount> {
    const segments = Object.keys(counts) as CustomersSegment[];
    return Object.fromEntries(
        segments.map((segment) => [
            segment,
            {
                value: String(counts[segment]),
                title: loadedCountTitle(SEGMENT_TITLE[segment], scope, CUSTOMERS_NOUN),
            },
        ]),
    ) as Record<CustomersSegment, PillCount>;
}

/** El aviso bajo las píldoras mientras queden páginas; `null` cuando lo cargado es todo. */
export function loadedCustomersNote(input: {
    scope: LoadedScope;
    localFilters: string[];
    localSort: boolean;
}): string | null {
    const { scope, localFilters, localSort } = input;
    if (!scope.hasMore) return null;
    const partial = ['las píldoras', ...localFilters.map((label) => `el filtro de ${label}`)];
    if (localSort) partial.push('el orden por columna');
    const loaded = countWithNoun(scope.loaded, CUSTOMERS_NOUN);
    const subject = joinEs(partial);
    return `Llevas ${loaded} cargados. ${subject[0].toUpperCase()}${subject.slice(1)} solo miran lo cargado. Usa «Cargar más» al pie de la tabla para traer el resto.`;
}

/** Sin filas: la cartera vacía no es lo mismo que una búsqueda o un filtro sin coincidencias. */
export function customersEmptyNote(input: {
    hasActiveFilters: boolean;
    hasNextPage: boolean;
    /** El nombre que se mandó al servidor, si fue lo único que dejó la tabla vacía. */
    nameQuery: string | null;
}): string {
    const { hasActiveFilters, hasNextPage, nameQuery } = input;
    if (!hasActiveFilters) return 'Todavía no tienes clientes: aquí aparece quien te compre.';
    if (nameQuery !== null && !hasNextPage) {
        return `Ningún cliente de tu cartera se llama «${nameQuery}…».`;
    }
    if (hasNextPage) {
        return 'Ningún cliente cargado coincide con los filtros; usa «Cargar más» para traer el resto.';
    }
    return 'Ningún cliente coincide con los filtros.';
}

export interface CustomersListErrorCopy {
    title: string;
    description: string;
    /** Falso: repetir la misma consulta da el mismo 400. */
    canRetry: boolean;
}

/** Qué decir cuando la primera página no llega, según el `code` del silo. */
export function customersListErrorCopy(error: unknown): CustomersListErrorCopy {
    if (error instanceof ApiError && error.code === 'VALIDATION_ERROR') {
        return {
            title: 'Revisa la búsqueda.',
            description: 'Escribe el inicio del nombre, de 80 caracteres o menos.',
            canRetry: false,
        };
    }
    return {
        title: 'No se pudo leer tu cartera.',
        description: 'Vuelve a intentarlo; si sigue igual, avísale a tu gerente.',
        canRetry: true,
    };
}

const INVALID_NEXT_TOKEN = 'INVALID_NEXT_TOKEN';

/** El cursor venció: la siguiente página no sale con él y la lista se vuelve a pedir desde cero. */
export function mustRestartCustomers(error: unknown): boolean {
    return error instanceof ApiError && error.code === INVALID_NEXT_TOKEN;
}

/** Por qué no llegó la página siguiente; lo ya cargado se queda. */
export function customersNextPageReason(error: unknown): string {
    if (mustRestartCustomers(error)) {
        return 'la lista cambió mientras la recorrías; «Reintentar» la vuelve a cargar desde el inicio';
    }
    return error instanceof ApiError ? error.msg : INTERNAL_ERROR_MESSAGE;
}

/** «a», «a y b», «a, b y c». */
function joinEs(items: string[]): string {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}
